import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { Vertical } from 'atoms/layout/flex';
import { LinkBox, LinkOther, LinkOverlay } from 'atoms/navigation/Box';
import { Card } from 'components/card/Card';
import { CardLockerOverlay } from 'components/card/CardLockerOverlay';
import { LoanWrapper } from 'components/card/Loan';
import { StrokedCard } from 'components/card/StrokedCard';

import type { PlayerAppearance_so5Appearance } from './__generated__/index.graphql';

const PictureWrapper = styled(Vertical).attrs({ center: true })`
  aspect-ratio: var(--card-aspect-ratio);
  width: 100%; /** necessary for the aspect-ratio to work properly while loading the image */
  position: relative;
`;

const Picture = styled.div`
  width: 100%;
  border: 1.5px solid transparent;
`;

const CardWrapper = styled(Vertical).attrs({ center: true })`
  width: 100%;
  transition: transform 200ms ease-in-out;
  justify-content: flex-start;
  &:not(:disabled) {
    &:hover,
    &:focus {
      transform: scale(1.05);
    }
  }
`;

const CardFooter = styled.footer`
  display: flex;
  align-items: center;
  gap: var(--half-unit);
`;

const PlayerPicture = styled.div`
  position: relative;
  width: 100%;
`;

const Top = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-areas: 'topLeft topMiddle .';
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  transform: translateY(-50%);
`;
const TopLeft = styled.div`
  grid-area: topLeft;
  transform: translateX(calc(-1 * var(--half-unit)));
`;
const TopMiddle = styled.div`
  grid-area: topMiddle;
  display: inline-flex;
  justify-content: center;
`;
const BottomBadge = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  transform: translateY(25%);
`;

type Props = {
  captain?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  appearance: PlayerAppearance_so5Appearance;
  score?: ReactNode;
  topBadge?: ReactNode;
  bottomBadge?: ReactNode;
  displayLocked?: boolean;
};

export const PlayerAppearance = ({
  appearance,
  disabled,
  onClick,
  captain,
  topBadge,
  bottomBadge,
  score,
  displayLocked,
}: Props) => {
  return (
    <LinkBox>
      <CardWrapper>
        <PictureWrapper>
          <StrokedCard active={!!captain} color="var(--c-rewards-pink)">
            <Picture>
              <LoanWrapper card={appearance.anyCard}>
                <LinkOverlay
                  as="button"
                  onClick={onClick}
                  disabled={!onClick || disabled}
                  type="button"
                >
                  <PlayerPicture>
                    <Card card={appearance} width={160} />
                    <CardLockerOverlay active={!!displayLocked} />
                  </PlayerPicture>
                </LinkOverlay>
              </LoanWrapper>
            </Picture>
          </StrokedCard>
          {!!bottomBadge && (
            <BottomBadge>
              <LinkOther as="div">{bottomBadge}</LinkOther>
            </BottomBadge>
          )}
        </PictureWrapper>
        {!!topBadge && (
          <Top>
            {!!captain && (
              <TopLeft>
                <LinkOther as="div">{captain}</LinkOther>
              </TopLeft>
            )}
            <TopMiddle>
              <LinkOther as="div">{topBadge}</LinkOther>
            </TopMiddle>
          </Top>
        )}
        <CardFooter>
          <LinkOther as="div">{score}</LinkOther>
        </CardFooter>
      </CardWrapper>
    </LinkBox>
  );
};

PlayerAppearance.fragments = {
  so5Appearance: gql`
    fragment PlayerAppearance_so5Appearance on So5AppearanceInterface {
      id
      cardBorderRadius
      anyCard {
        slug
        ...LoanWrapper_anyCard
      }
      ...Card_so5Appearance
    }
    ${Card.fragments.so5Appearance}
    ${LoanWrapper.fragments.anyCard}
  ` as TypedDocumentNode<PlayerAppearance_so5Appearance>,
};
